import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import type { IProductType, TEcoSystemGuide } from "@/typings/Props";
import Header from "@/components/main/Header";
import Footer from "@/components/main/Footer";
import Banner from "@/components/products/detail/Banner";
import KeyFeature from "@/components/products/detail/KeyFeature";
import ResultSheet from "@/components/products/detail/ResultSheet";
import DetailInfo from "@/components/products/detail/DetailInfo";
import YouTube from "@/components/products/detail/Youtube";
import OtherModels from "@/components/products/detail/OtherModels";
import EcoSystem from "@/components/products/detail/EcoSystem";
import useProduct from "@/hooks/useProduct";
import { media } from "@/styles/media";
import { theme } from "@/styles/theme";

export default function ProductDetail() {
  const router = useRouter();
  const { t } = useTranslation();
  const { id } = router.query;
  const products = useProduct();

  const [product, setProduct] = useState<IProductType | null>(null);

  useEffect(() => {
    products.fetchProductsAsync();
  }, []);

  const finedProduct = products.state.products.find((p) => p.link === id);
  useEffect(() => {
    function init(newProduct: IProductType) {
      setProduct(null);
      setTimeout(() => setProduct(newProduct), 10);
    }
    if (finedProduct) init(finedProduct);
  }, [finedProduct]);

  if (products.state.loading) {
    return <>loading...</>;
  }

  if (!product) {
    return (
      <>
        <Header />
        <Footer />
      </>
    );
  }

  const INTERGRATIONS: {
    ecoSystem: TEcoSystemGuide[];
  }[] = [
    {
      ecoSystem: [
        {
          id: 1,
          title: t("products.ecosystem-0-1.title"),
          desc: t("products.ecosystem-0-1.desc"),
          coord: {
            x: "20%",
            y: "83%",
          },
        },
        {
          id: 2,
          title: t("products.ecosystem-0-2.title"),
          desc: t("products.ecosystem-0-2.desc"),
          coord: {
            x: "80%",
            y: "70%",
          },
        },
        {
          id: 3,
          title: t("products.ecosystem-0-3.title"),
          desc: t("products.ecosystem-0-3.desc"),
          coord: {
            x: "37%",
            y: "38%",
          },
        },
        {
          id: 4,
          title: t("products.ecosystem-0-4.title"),
          desc: t("products.ecosystem-0-4.desc"),
          coord: {
            x: "62%",
            y: "42%",
          },
        },
        {
          id: 5,
          title: t("products.ecosystem-0-5.title"),
          desc: t("products.ecosystem-0-5.desc"),
          coord: {
            x: "60%",
            y: "63%",
          },
        },
        {
          id: 6,
          title: t("products.ecosystem-0-6.title"),
          desc: t("products.ecosystem-0-6.desc"),
          coord: {
            x: "65%",
            y: "80%",
          },
        },
        {
          id: 7,
          title: t("products.ecosystem-0-7.title"),
          desc: t("products.ecosystem-0-7.desc"),
          coord: {
            x: "75%",
            y: "51%",
          },
        },
        {
          id: 8,
          title: t("products.ecosystem-0-8.title"),
          desc: t("products.ecosystem-0-8.desc").replaceAll("{{product.title}}", t(product.title)),
          coord: {
            x: "47%",
            y: "50%",
          },
        },
      ],
    },
    {
      ecoSystem: [
        {
          id: 1,
          title: t("products.ecosystem-1-1.title"),
          desc: t("products.ecosystem-1-1.desc"),
          coord: {
            x: "26%",
            y: "86%",
          },
        },
        {
          id: 2,
          title: t("products.ecosystem-1-2.title"),
          desc: t("products.ecosystem-1-2.desc"),
          coord: {
            x: "72%",
            y: "69%",
          },
        },
        {
          id: 3,
          title: t("products.ecosystem-1-3.title"),
          desc: t("products.ecosystem-1-3.desc").replaceAll("{{product.title}}", t(product.title)),
          coord: {
            x: "41%",
            y: "22%",
          },
        },
        {
          id: 4,
          title: t("products.ecosystem-1-4.title"),
          desc: t("products.ecosystem-1-4.desc"),
          coord: {
            x: "54%",
            y: "44%",
          },
        },
        {
          id: 5,
          title: t("products.ecosystem-1-5.title"),
          desc: t("products.ecosystem-1-5.desc"),
          coord: {
            x: "54%",
            y: "62.5%",
          },
        },
        {
          id: 6,
          title: t("products.ecosystem-1-6.title"),
          desc: t("products.ecosystem-1-6.desc"),
          coord: {
            x: "53%",
            y: "79.5%",
          },
        },
        {
          id: 7,
          title: t("products.ecosystem-1-7.title"),
          desc: t("products.ecosystem-1-7.desc"),
          coord: {
            x: "67%",
            y: "52.8%",
          },
        },
      ],
    },
  ];

  const ecoSystem =
    typeof product?.ecoSystem?.intergrationIdx === "number"
      ? INTERGRATIONS[product.ecoSystem.intergrationIdx].ecoSystem
      : null;

  return (
    <>
      <Header />
      <DetailWrapper>
        <Banner
          title={product.title}
          description={product.description}
          longDesc={product.longDesc}
          Image={product.hdImage}
        />
        {product.keyFeature && (
          <>
            <P>
              <span id="stress">{t(product.title)}</span> {t("menu.products.key-features")}
            </P>
            <KeyFeatureWrapper>
              {product.keyFeature?.map((data) => {
                return <KeyFeature key={data.id} title={data.title} desc={data.desc} image={data.image} />;
              })}
            </KeyFeatureWrapper>
          </>
        )}
        {product.resultSheet && (
          <>
            <P dangerouslySetInnerHTML={{ __html: t("menu.products.result-sheet.result-sheet-title") }} />
            <StyledResultSheet
              $marginTop="65px"
              $contentHeight="570px"
              $imgWidth="51%"
              $textWidth="612px"
              isProduct={true}
              datas={product.resultSheet}
            />
          </>
        )}
        {product.detailImage && <DetailInfo {...product} />}
        {product.youtube && <YouTube {...product} />}
        {product.ecoSystem && (
          <EcoSystem
            ecoImg={product.ecoSystem.ecoImage}
            modalImg={product.ecoSystem.modalImage}
            title={t(product.title)}
            desc={t(product.ecoSystem.desc)}
            ecoSystem={ecoSystem}
          />
        )}
        <StyledOtherModels>
          <OtherModels />
        </StyledOtherModels>
      </DetailWrapper>
      <Footer />
    </>
  );
}

const DetailWrapper = styled.div`
  overflow: hidden;
  ${media.tablet`
      padding: 0;
  `}
`;

const KeyFeatureWrapper = styled.div`
  padding: 0 40px;
  display: flex;
  flex-wrap: wrap;
  gap: 30px;

  ${media.desktop`
    gap: 20px;
  `}

  ${media.tablet`
    padding: 0 28px;
    flex-direction: column;
    justify-content: initial;
    gap: unset;
  `}
`;

const P = styled.p`
  text-align: center;
  font-weight: 700;
  font-size: 44px;
  padding: 52px 0;
  margin-top: 30px;
  color: ${theme.colors.text.grayDark};
  & #stress {
    color: ${theme.colors.primary};
  }
`;

const StyledResultSheet = styled(ResultSheet)`
  .content-wrapper {
    width: 100%;
    margin-top: 65px;
    background-color: ${theme.colors.background.white};
    ${media.tablet`
      background-color: initial;
      padding: 0;
    `};
  }
  .left-area {
    margin: 0 0 20px 0;
    padding: 0 80px;
    max-width: 600px;
    ${media.tablet`
      margin: initial;
      max-width: initial;
      width: 100%;
      display: flex;
      justify-content: center;
      background-color: #fff;
    `};
  }
  .right-area {
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin: 0;
    max-width: 600px;
    ${media.tablet`
      padding-top: unset;
      padding: 120px 60px;
      width: 100%;
      height: 25%;
    `}
  }

  img {
    width: 100%;
  }
  ul {
    border-bottom: 0.5px solid rgba(151, 27, 47, 0.3);
  }
  li {
    padding: 15px 13px;
  }
  button {
    text-underline-offset: 16px !important;
  }
`;

const StyledOtherModels = styled.div`
  /* width: 100%;
  padding: 0 28px;

  .title {
    padding: 0;
    padding-top: 48px;
  }
  .button {
    margin: 15px auto 52px 0;
  }
  .item-wrapper {
    width: calc(50% - 8px);
  } */
`;
